import { appLocationId, S3_URL } from "config/envConfig";
import { OfflineScreen } from "offline/OfflineScreen";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import apiList from "services/apiList";
import { callApi } from "services/callApi";
import { ThemeProvider as StyledThemeProvider } from "styled-components";
import { defaultColorTheme } from "theme/colors";
import {
  getLocalStorage,
  getSessionStorage,
  setLocalStorage,
  setSessionStorage,
} from "utils/storageUtil";
import "./App.css";
import MainProvider from "./providers/mainProvider";
import MainSwitch from "./routes/mainSwitch";
import { GlobalStyles } from "./theme/globalStyle";
import theme from "./theme/schema.json";
import {
  ALLOW_ALL_STORES_KEY,
  DATA_REFERENCE,
  KIOSK_DETAILS,
  KIOSK_ID_KEY,
  STORE_LIST_KEY,
  STORE_WAYPOINTS_KEY,
} from "./utils/Constants";
import { syncAppConfig } from "config/appConfigHelper";
import { postBridgeMessage } from "utils/bridgeMessage";

function App() {
  const location = useLocation();
  const { pathname } = location;

  const [isRerender, setIsRerender] = useState(false);
  const [config, setConfig] = useState(getLocalStorage("config") || {});

  const updateConfig = () => {
    const newConfig = getLocalStorage("config");
    setConfig(newConfig);
  };

  function applyTheme(theme) {
    const root = document.documentElement;

    function setVar(name, value) {
      // convert camelCase to kebab-case for CSS variables
      const cssVar = `--${name.replace(
        /[A-Z]/g,
        (m) => "-" + m.toLowerCase()
      )}`;
      root.style.setProperty(cssVar, value);
    }

    function processObject(obj, prefix = "") {
      for (const [key, value] of Object.entries(obj)) {
        if (value && typeof value === "object" && !value.hex) {
          // nested object (like text, state)
          processObject(value, `${prefix}${key}-`);
        } else if (
          value &&
          typeof value === "object" &&
          (value.hex || value.hsl)
        ) {
          // value has hex & hsl
          setVar(`${prefix}${key}`, value.hex);
          setVar(`${prefix}${key}-hsl`, value.hsl);
        } else if (typeof value === "string" || typeof value === "number") {
          setVar(`${prefix}${key}`, value);
        }
      }
    }

    processObject(theme);
  }

  useEffect(() => {
    const storageListener = () => {
      updateConfig();
    };

    window.addEventListener("storage", storageListener);

    return () => {
      window.removeEventListener("storage", storageListener);
    };
  }, []);

  useEffect(() => {
    const disableContextMenu = (e) => e.preventDefault();
    const disableDragStart = (e) => e.preventDefault();
    const disableSelectStart = (e) => e.preventDefault();

    document.addEventListener("contextmenu", disableContextMenu);
    document.addEventListener("dragstart", disableDragStart);
    document.addEventListener("selectstart", disableSelectStart);

    return () => {
      document.removeEventListener("contextmenu", disableContextMenu);
      document.removeEventListener("dragstart", disableDragStart);
      document.removeEventListener("selectstart", disableSelectStart);
    };
  }, []);

  const getLocationFromURL = () => {
    const host = window.location.hostname;

    if (host === "localhost") {
      return "cph";
    }

    const domain = host.split(".")[0];
    const pieces = domain.split("-");

    return pieces[pieces.length - 1];
  };

  useEffect(() => {
    const initTheme = async (locationId) => {
      try {
        const singleStoreCtx = getSingleStoreThemeContext();

        if (singleStoreCtx?.waypointId) {
          const storeTheme = await fetchColorTheme(
            singleStoreCtx.locationId,
            singleStoreCtx.waypointId
          );
          if (storeTheme) {
            applyTheme(storeTheme);
            return;
          }
        }

        const themeData = await fetchColorTheme(locationId);
        applyTheme(themeData || defaultColorTheme);
      } catch (err) {
        console.log("err", err);
        applyTheme(defaultColorTheme);
      }
    };

    const location = getLocationFromURL() || "CPH";
    const locationId = location.toUpperCase() + "LOCID";
    initTheme(locationId);
  }, []);

  useEffect(() => {
    function handleMessage(event) {
      try {
        const msg =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;

        if (msg?.type === "add_item") {
          console.log("[KioskApp] add_item received", msg.payload);
        }
      } catch (e) {
        console.log("Kiosk bridge parse error", e);
      }
    }

    document.addEventListener("message", handleMessage);
    window.addEventListener("message", handleMessage);

    let webviewHandler;

    if (window.chrome?.webview) {
      webviewHandler = (e) => handleMessage(e);
      window.chrome.webview.addEventListener("message", webviewHandler);
    }

    return () => {
      document.removeEventListener("message", handleMessage);
      window.removeEventListener("message", handleMessage);

      if (window.chrome?.webview && webviewHandler) {
        window.chrome.webview.removeEventListener("message", webviewHandler);
      }
    };
  }, []);

  useEffect(() => {
    postBridgeMessage({ type: "page_ready" });
  }, [pathname]);

  useEffect(() => {
    const interval = setInterval(() => {
      postBridgeMessage({ type: "heartbeat" });
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let blankCount = 0;

    const interval = setInterval(() => {
      try {
        const appRoot = document.querySelector("#root");
        const hasVisibleContent =
          appRoot != null &&
          appRoot.childElementCount > 0 &&
          (document.body?.getBoundingClientRect().height > 100 ||
            window.innerHeight > 100);

        if (!hasVisibleContent) {
          blankCount += 1;
        } else {
          blankCount = 0;
        }

        if (blankCount >= 10) {
          postBridgeMessage({ type: "blank_detected" });
          blankCount = 0;
        }
      } catch {}
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const data = (() => {
      try {
        return JSON.parse(localStorage.getItem(DATA_REFERENCE)) || {};
      } catch (e) {
        return {};
      }
    })();

    if (data?.[KIOSK_ID_KEY]) {
      getKioskDetails(data[KIOSK_ID_KEY]);
    }
  }, []);

  useEffect(() => {
    async function handleEvent(event) {
      const parsedData = event.detail ?? {};
      console.log("[KioskApp] kiosk-message received", parsedData);

      localStorage.setItem(DATA_REFERENCE, JSON.stringify(parsedData));

      if (parsedData?.kioskId) {
        await getKioskDetails(parsedData.kioskId);
      }

      setIsRerender((prev) => !prev);
    }

    window.addEventListener("kiosk-message", handleEvent);

    return () => window.removeEventListener("kiosk-message", handleEvent);
  }, [isRerender]);

  useEffect(() => {
    const initializeApp = async () => {
      await syncAppConfig();
      // MoEngage init logic will now have access to MOENGAGE_APP_ID from environment variables
    };
    initializeApp();
  }, []);

  const getKioskDetails = async (kioskId = "kiosk1") => {
    const apiURL = apiList.details + `?kioskId=${kioskId}`;
    const response = await callApi.get(apiURL);

    if (response.status === 200) {
      const data = response.data;
      setLocalStorage(KIOSK_DETAILS, data);
    }
  };

  const updateConfigData = (newData) => {
    let configData = {};
    try {
      configData = getSessionStorage("configData") || {};
    } catch (err) {
      console.log(err);
    }

    const updatedConfig = {
      ...configData,
      ...newData,
    };

    setSessionStorage("configData", updatedConfig);
  };

  const fetchAgeRestrictionPromptData = async () => {
    try {
      let configData = {};
      try {
        configData = getSessionStorage("configData") || {};
      } catch (e) {}
      if (configData?.ageRestrictionPromptData) return;
      const response = await callApi.get(apiList.cms.alcoholAlert);
      if (response?.data) {
        updateConfigData({
          ageRestrictionPromptData: response?.data?.[0]?.attributes,
        });
        return true;
      }

      return false;
    } catch (e) {
      return false;
    }
  };

  const fetchPrivacyPolicy = async () => {
    try {
      let configData = {};
      try {
        configData = getSessionStorage("configData") || {};
      } catch (e) {}
      if (configData?.privacyPolicy) return;
      const response = await callApi.get(apiList.pages?.privacyPolicy);
      if (response?.html) {
        updateConfigData({ privacyPolicy: response.html });
        return true;
      }

      return false;
    } catch (e) {
      return false;
    }
  };

  const fetchTermsOfService = async () => {
    try {
      let configData = {};
      try {
        configData = getSessionStorage("configData") || {};
      } catch (e) {}
      if (configData?.termsOfService) return;
      const response = await callApi.get(apiList.pages?.termsOfService);
      if (response?.html) {
        updateConfigData({ termsOfService: response.html });
        return true;
      }

      return false;
    } catch (e) {
      return false;
    }
  };

  const fetchDisclaimer = async () => {
    try {
      let configData = {};
      try {
        configData = getSessionStorage("configData") || {};
      } catch (e) {}
      if (configData?.disclaimer) return;
      const response = await callApi.get(apiList.pages?.disclaimer);
      if (response?.html) {
        updateConfigData({ disclaimer: response.html });
        return true;
      }

      return false;
    } catch (e) {
      return false;
    }
  };

  const fetchColorTheme = async (id, waypointId = null) => {
    try {
      const locationId = id || appLocationId;
      const baseUrl = waypointId
        ? `${S3_URL}/${locationId}/kiosk/${waypointId}`
        : `${S3_URL}/${locationId}/kiosk`;
      const apiURL = `${baseUrl}/theme/theme.json`;

      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Timeout exceeded")), 10000)
      );
      const fetcher = callApi.getS3(apiURL).then((res) => res.json());

      const themeResponse = await Promise.race([timeout, fetcher]);
      // console.log("themeResponse", JSON.stringify(themeResponse))
      return themeResponse;
    } catch (err) {
      console.error("Error fetching color theme:", err);
      return null;
    }
  };

  const getSingleStoreThemeContext = () => {
    try {
      const data = JSON.parse(localStorage.getItem(DATA_REFERENCE)) || {};
      const allowAll =
        data && data[ALLOW_ALL_STORES_KEY] !== undefined
          ? data[ALLOW_ALL_STORES_KEY]
          : true;
      const storeList =
        data && Array.isArray(data[STORE_LIST_KEY]) ? data[STORE_LIST_KEY] : [];
      const waypoints = (data && data[STORE_WAYPOINTS_KEY]) || {};

      if (allowAll || storeList.length !== 1) return null;

      const storeId = storeList[0];
      const waypointEntry = waypoints[storeId];
      if (waypointEntry?.waypointId) {
        return {
          waypointId: waypointEntry.waypointId,
          locationId: waypointEntry.locationId || appLocationId,
        };
      }
    } catch (e) {
      console.log("getSingleStoreThemeContext error", e);
    }
    return null;
  };

  useEffect(() => {
    const initialCall = async () => {
      await fetchAgeRestrictionPromptData();
      await fetchPrivacyPolicy();
      await fetchTermsOfService();
      await fetchDisclaimer();
    };
    initialCall();
  }, []);

  useEffect(() => {
    // Disable Ctrl + Wheel (Zoom)
    const handleWheel = (e) => {
      if (e.ctrlKey) e.preventDefault();
    };

    // Disable trackpad pinch gestures
    const handleGesture = (e) => {
      e.preventDefault();
    };

    // Disable keyboard zoom shortcuts (Ctrl + "+", "-", "0")
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && ["+", "-", "0"].includes(e.key)) {
        e.preventDefault();
      }
    };

    // For browsers that emit gesture events
    document.addEventListener("gesturestart", handleGesture);
    document.addEventListener("gesturechange", handleGesture);
    document.addEventListener("gestureend", handleGesture);

    document.addEventListener("wheel", handleWheel, { passive: false });
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("gesturestart", handleGesture);
      document.removeEventListener("gesturechange", handleGesture);
      document.removeEventListener("gestureend", handleGesture);
      document.removeEventListener("wheel", handleWheel);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <MainProvider>
      <StyledThemeProvider theme={theme.data}>
        <OfflineScreen />
        <GlobalStyles />
        <MainSwitch />
      </StyledThemeProvider>
    </MainProvider>
  );
}

export default App;
